import React, { useEffect, useRef } from 'react';
import HeroSection from '../components/HeroSection';
import { YEARS_SINCE_OPENING } from '../utils/clinicInfo';

const ChildrenPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    // fullPage.js 제거
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    document.body.classList.remove('fullpage-enabled');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // 페이지 진입 시 맨 위로 스크롤
  useEffect(() => {
    // DOM 업데이트 후 스크롤하도록 지연
    const timer = setTimeout(() => {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="children-page-container">
      <HeroSection 
        title="어린이 치료"
        subtitle="우리 아이의 평생 건강한 치아, 지금부터 시작하세요"
        backgroundImage="/assets/어린이치료배경.jpg"
        brandText="한앤손 부부치과"
        ref={heroRef}
      />

      <div className="max-w-7xl mx-auto px-2 md:px-4 py-6 md:py-12">
        <div className="space-y-8 md:space-y-16">
          {/* 인사말 섹션 */}
          <div className="text-center mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-primary mb-2 md:mb-4">어린이 치료</h2>
            <p className="text-base md:text-xl text-gray-600">키즈룸은 없지만 가장 많은 아이들이 찾는 치과</p>
          </div>

          {/* 병원 특징 소개 */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg mb-8 md:mb-16">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-10 shadow-md">
                <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6 md:mb-8 text-center">안녕하세요</h3>
                <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 md:space-y-8">
                  <p className="text-center md:text-left">
                    저희 병원은 아마 혁신도시 치과 중 유일하게 키즈룸과 소아전용진료실이 없는 치과일 것입니다. 
                    하지만 혁신도시 치과 중 가장 많은 어린이들이 치료받는 치과가 아닐까 싶습니다.
                  </p>
                  <p className="text-center md:text-left">
                    개원을 앞두고 인테리어 구상 중 키즈대기실과 소아진료체어를 구상하지 않았던 이유 중 하나는 
                    혁신도시에 이렇게 많은 아이들이 있는걸 몰랐고, 또 이곳이 이렇게 출생률이 높은 지역이 될 것이라 
                    예상하지 못했다는 것입니다. 하지만 개원 첫날부터 예상치 못했던 어린이 환자들 덕분에 
                    병원이 시끌벅적했던 기억이 나네요.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 성장 이야기 */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg mb-8 md:mb-16">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-10 shadow-md">
                <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6 md:mb-8 text-center">함께 성장한 {YEARS_SINCE_OPENING}년</h3>
                <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 md:space-y-8">
                  <p className="text-center md:text-left">
                    참 많은 아이들이 다녀갔고, 많은 아이들이 성장하고 있습니다. 
                    개원 첫해 제가 출산 3-4일 전 만삭의 몸으로 바닥에 누워 울고불고하는 걸 겨우 달래가며 
                    충치치료했던 4살 아이가 이제는 10살이 되어 진료실에 씩씩하게 혼자 들어와 마취주사까지 맞습니다.
                  </p>
                  <p className="text-center md:text-left">
                    저도 만삭의 몸으로, 또 같이 만삭의 몸으로 치료 받던 환자분의 아이가 벌써 첫 번째 유치를 발치했습니다. 
                    교복을 입고 교정치료를 받던 중학생 아이들이 어느덧 대학생이 되고, 
                    군입대를 하여 휴가 나와 정기검진을 받기도 하죠.
                  </p>
                  <p className="text-center md:text-left">
                    개원 당시 뱃속에 있던 저희 아이가 초등학교 입학을 앞두고 있듯이, 
                    병원에 다녀간 많은 아이들이 그만큼 컸고, 성장하는 모습을 보는 재미도 쏠쏠합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 치료 철학과 실제 */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg mb-8 md:mb-16">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-10 shadow-md">
                <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6 md:mb-8 text-center">부모의 마음으로</h3>
                <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 md:space-y-8">
                  <p className="text-center md:text-left">
                    개원 전에도 많은 소아환자들을 봤지만, 개원 후 부모가 되어 바라보는 아이들은 더 각별합니다. 
                    무서워하고 우는 아이의 마음을 한번 더 고려하고, 또 그 모습을 바라보는 부모의 마음을 이해하는 것이 
                    소아진료에 대한 제 철학이 되었습니다.
                  </p>
                  <p className="text-center md:text-left">
                    대부분 아이들의 진료는 10분 내외로 끝납니다. 그렇게 걱정하는 신경치료도 정작 치료시간은 10분 정도입니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 치료 약속 */}
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-10 shadow-md">
                <h3 className="text-2xl md:text-3xl font-bold text-primary mb-6 md:mb-8 text-center">저희의 약속</h3>
                <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 md:space-y-8">
                  <p className="text-center md:text-left">
                    아이를 믿고, 또 부모님이 의료진을 믿어주실 때 진료는 원활히 이루어질 수 있고, 
                    가능한 저희는 모든 아이를 치료하려 노력합니다. 단지 의료진이 힘들고 귀찮다는 이유만으로 
                    여느 치과들처럼 무조건 소아치과 치료를 권유드리지 않습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 어린이 치료 특별함 */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl md:rounded-3xl p-4 md:p-10 shadow-lg mb-8 md:mb-16">
            <div className="text-center mb-6 md:mb-10">
              <h3 className="text-xl md:text-3xl font-bold text-primary mb-2 md:mb-4">한앤손 부부치과 어린이 치료의 특별함</h3>
              <p className="text-sm md:text-lg text-gray-600">{YEARS_SINCE_OPENING}년간 지역의 아이들과 함께 성장한 경험</p>
            </div>
            
            <div className="max-w-4xl mx-auto space-y-4 md:space-y-8">
              <div className="grid md:grid-cols-2 gap-4 md:gap-8">
                <div className="bg-blue-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                  <div className="flex items-center mb-3 md:mb-4">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center mr-2 md:mr-3">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m3 0H4a1 1 0 00-1 1v16a1 1 0 001 1h16a1 1 0 001-1V5a1 1 0 00-1-1z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h6v6H9z" />
                      </svg>
                    </div>
                    <h4 className="text-sm md:text-lg font-semibold text-primary">아이와 함께 자란 경험</h4>
                  </div>
                  <p className="text-xs md:text-base text-gray-700">
                    {YEARS_SINCE_OPENING}년간 지역의 아이들이 성장하는 모습을 지켜보며 함께 성장한 풍부한 경험을 가지고 있습니다.
                  </p>
                </div>
                
                <div className="bg-pink-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                  <div className="flex items-center mb-3 md:mb-4">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-secondary rounded-full flex items-center justify-center mr-2 md:mr-3">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <h4 className="text-sm md:text-lg font-semibold text-secondary">지역 주민으로서의 마음</h4>
                  </div>
                  <p className="text-xs md:text-base text-gray-700">
                    이곳 충북혁신도시 지역 주민으로서 우리 동네 아이들을 더욱 소중히 생각하고 돌봅니다.
                  </p>
                </div>
                
                <div className="bg-green-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                  <div className="flex items-center mb-3 md:mb-4">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-green-600 rounded-full flex items-center justify-center mr-2 md:mr-3">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h4 className="text-sm md:text-lg font-semibold text-green-600">부모의 마음으로</h4>
                  </div>
                  <p className="text-xs md:text-base text-gray-700">
                    부모로서 아이를 사랑하는 마음이 더해질 때 더 좋은 진료가 이루어진다는 믿음으로 치료합니다.
                  </p>
                </div>
                
                <div className="bg-purple-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                  <div className="flex items-center mb-3 md:mb-4">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-600 rounded-full flex items-center justify-center mr-2 md:mr-3">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h4 className="text-sm md:text-lg font-semibold text-purple-600">진심과 경험의 조합</h4>
                  </div>
                  <p className="text-xs md:text-base text-gray-700">
                    치료는 경험이 묻어나고 진심이 더해질 때 가장 좋은 결과를 낼 수 있다고 믿습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 어린이 치료 철학 */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl md:rounded-3xl p-4 md:p-10 shadow-lg mb-8 md:mb-16">
            <div className="text-center mb-6 md:mb-10">
              <h3 className="text-xl md:text-3xl font-bold text-primary mb-2 md:mb-4">어린이 치료 철학</h3>
              <p className="text-sm md:text-lg text-gray-600">우리 아이를 내 아이처럼 사랑하는 마음으로</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <svg className="w-6 h-6 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h4 className="text-sm md:text-xl font-bold text-primary mb-2 md:mb-4">부모의 마음</h4>
                <p className="text-xs md:text-base text-gray-600 leading-relaxed">
                  아이를 내 아이처럼 사랑하는 마음으로 
                  한 명 한 명 소중히 돌봅니다.
                </p>
              </div>

              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <svg className="w-6 h-6 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h.01" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9h.01" />
                  </svg>
                </div>
                <h4 className="text-sm md:text-xl font-bold text-primary mb-2 md:mb-4">무서워하지 않는 치료</h4>
                <p className="text-xs md:text-base text-gray-600 leading-relaxed">
                  아이들이 무서워하지 않도록 친근하고 
                  부드러운 치료 환경을 만들어드립니다.
                </p>
              </div>

              <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <svg className="w-6 h-6 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="text-sm md:text-xl font-bold text-primary mb-2 md:mb-4">평생 건강한 치아</h4>
                <p className="text-xs md:text-base text-gray-600 leading-relaxed">
                  어릴 때부터 올바른 관리로 
                  평생 건강한 치아를 만들어드립니다.
                </p>
              </div>
            </div>
          </div>

          {/* 상담 예약 CTA */}
          <div className="bg-primary text-white rounded-2xl p-10 text-center">
            <h3 className="text-2xl font-semibold mb-4">우리 아이 치아 건강 상담받아보세요</h3>
            <p className="text-base md:text-xl mb-4 md:mb-8 text-white/90 leading-relaxed">
              아이를 사랑하는 마음으로, {YEARS_SINCE_OPENING}년간의 경험과 진심을 담아<br />
              우리 아이의 평생 건강한 치아를 만들어드리겠습니다.
            </p>
            <a 
              href="tel:043-534-3434" 
              className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              상담 예약하기
            </a>
          </div> 
        </div>
      </div>
    </div>
  );
};

export default ChildrenPage; 